import styled from "@emotion/styled";
import { FC, useState } from "react";
import { useSelector } from "react-redux";

import {
  DesignToken,
  Heading,
  Link,
  PromoBannerV2,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import workflowsImage from "assets/img/workflows-banner@2x.png";
import Block from "components/layout/Block";
import { newFeaturesCampaigns } from "constants/campaigns";
import { TextHelpCenter } from "constants/help-center";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { selectLoggedInAgentPermission } from "store/entities/agents";
import { withVar } from "themes/helpers";

export const IframeOnboardingBanner: FC = () => {
  const { featureFlagValue: hasClosedBanner, setAgentFlagCallback: markBannerAsClosed } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasClosedWorkflowsBanner.flag,
    newFeaturesCampaigns.hasClosedWorkflowsBanner.campaignStartDate
  );
  const [isBannerOpened, setBannerOpened] = useState(!hasClosedBanner);
  const userRole = useSelector(selectLoggedInAgentPermission);

  const trackBannerToggle = (isOpen: boolean): void => {
    trackEngagement("Section onboarding banner", EventPlace.Automations, {
      role: userRole,
      place: "rules",
      status: isOpen ? "show clicked" : "hide clicked",
    });
  };

  const handleBannerCloseClick = (): void => {
    markBannerAsClosed();
    setBannerOpened(false);
    trackBannerToggle(false);
  };

  const handleLearnMoreClick = (): void => {
    trackEngagement(`Rules: Learn more clicked`, EventPlace.Automations, {
      place: "rules",
      role: userRole,
      bannerOpened: isBannerOpened,
    });
  };

  return (
    isBannerOpened && (
      <Block maxWidth="1024px">
        <StyledPromoBanner
          onClose={handleBannerCloseClick}
          additionalContent={<img src={workflowsImage} alt="workflows image" />}
        >
          <Heading css={{ paddingBottom: withVar(SpacingToken.Spacing1) }}>What are rules?</Heading>
          <Text>
            Rules automate your tasks, triggering actions when specific conditions are met. They cut down on ticket
            management time, help you respond quicker and more accurately to customer requests, minimize errors, and
            free you up to tackle complex issues.{" "}
            <Link
              bold={false}
              href={TextHelpCenter.AUTOMATED_WORKFLOWS}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rules-onboarding-banner-learn-more-link"
              onClick={handleLearnMoreClick}
            >
              Learn more
            </Link>
          </Text>
        </StyledPromoBanner>
      </Block>
    )
  );
};

const StyledPromoBanner = styled(PromoBannerV2)`
  max-width: 940px;
  background: ${withVar(DesignToken.SurfaceGradient07)};
`;
